// DEPLOY: /var/www/woody.wtf/frontend/src/pages/SmcLab.js
// Admin "SMC Lab": backtest the SMC engine (backend/engines/smc_core.py +
// smc_backtest.py) with minimal input. Models: "BOS / CHoCH only" (hold the side of
// the last structure break, any timeframe incl. end-of-day), "Top-down" (bias / zones /
// entry timeframes, presets fast / scalp / day) and "Raid & shift". Every setting
// has a default and is remembered in this browser. An optional second date range
// runs the same settings untouched, to check a result holds on data it wasn't
// tuned on. Runs are background jobs (POST /api/smc/backtest, then poll
// GET /api/smc/result/<job>); job ids are kept in this browser so a run survives
// the phone sleeping or the page reloading. "Chart" runs the same settings over
// 1-5 days of one ticker and draws what the model saw (components/SmcChart.js).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';
import { ConditionRow, blankCond, serialiseCond } from '../components/ConditionEditor';
import SmcChart from '../components/SmcChart';

const HC = window.Highcharts;   // Highcharts Stock via CDN (public/index.html)
const STORE = 'smcLab.v4';
const JOBS = 'smcLab.jobs';
const FRAMES = ['1m', '5m', '10m', '15m', '30m', '1h', '2h', '3h', '4h'];
const SOURCES = [['prev_day', 'Prior day'], ['prev_week', 'Prior week'], ['overnight', 'Overnight'],
  ['opening_range', 'Opening range'], ['equal', 'Equal highs/lows'], ['htf_swing', 'Swing highs/lows']];
const TICKERS = { leveraged: ['QQQ:US'], single: ['QQQ:US', 'TQQQ:US', 'SQQQ:US'] };
const PRESETS = [['fast', 'Fast', '15m', '5m', '1m'], ['scalp', 'Scalp', '1h', '15m', '5m'], ['day', 'Day', '4h', '1h', '15m']];
const BREAKDOWNS = [['direction', 'Direction'], ['zone', 'Pullback reached a zone'], ['stop_from', 'Stop placed beyond'],
  ['sweep_source', 'Liquidity raided'], ['target_source', 'Liquidity targeted'], ['window', 'Entry window'],
  ['rr', 'Planned R:R'], ['risk_atr', 'Stop size (ATR)'], ['mfe_r', 'Best move in favour (R)'],
  ['entry_hour', 'Entry hour (NY)'], ['weekday', 'Weekday'], ['exit', 'Exit']];
const FUNNEL = { topdown: ['pullbacks', 'turns back', 'setups'], raid: ['raids', 'shifts', 'setups'], structure: ['CHoCH', 'BOS'] };
const CHART_DAYS = { '1h': 60, '2h': 60, '3h': 90, '4h': 90, '1d': 730 };   // mirrors smc_lab.CHART_DAYS_BY_TF

const MODEL_DEFAULTS = {
  structure: { trend_tf: null, liq_tf: '15m', exec_tf: '1h', windows: [['09:30', '16:00']], max_trades_per_window: 100, flat_eod: false },
  topdown: { trend_tf: '1h', liq_tf: '15m', exec_tf: '5m', windows: [['09:30', '15:45']], max_trades_per_window: 20, flat_eod: true },
  raid:    { trend_tf: '1h', liq_tf: '15m', exec_tf: '1m', windows: [['09:30', '11:00'], ['14:00', '15:00']], max_trades_per_window: 1, flat_eod: true },
};
const DEFAULTS = {
  model: 'structure', mode: 'leveraged', signals: ['QQQ:US'], from: '2016-01-01', to: '2024-12-31',
  testOn: false, testFrom: '2025-01-01', testTo: '2025-12-31', direction: 'both',
  ...MODEL_DEFAULTS.structure,
  start_equity: 100000, risk_pct: 1, max_positions: 2, max_exposure: 1,
  commission_per_share: 0.005, commission_min: 1, slippage_bps: 1, breakeven_r: 0,
  st: { swing_len: 3 },
  td: { hold: 'stay', entry: 'retest', require_zone: true, take_profit_r: 0, bias_swing_len: 3, zone_swing_len: 3,
        entry_swing_len: 2, zone_min_atr: 0.25, zone_max_bars: 50, stop_buffer_atr: 0.1, min_risk_atr: 1, expiry: 10 },
  raid: { sources: SOURCES.map(x => x[0]), opening_range_min: 30, level_max_days: 5, htf_swing_len: 3, eq_tol_atr: 0.15,
          ltf_swing_len: 2, sweep_window: 30, fvg_min_atr: 0.1, entry: 'ce', stop_buffer_atr: 0.1, min_rr: 2, expiry: 30 },
  filters: [],
};

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '24px 16px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '6px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', marginBottom: '16px' },
  title:   { fontSize: '15px', fontWeight: 600, marginBottom: '10px' },
  sub:     { fontSize: '12px', fontWeight: 600, margin: '14px 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" },
  row:     { display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' },
  label:   { display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" },
  check:   { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--text)', cursor: 'pointer' },
  input:   { padding: '6px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', background: 'var(--bg)', color: 'var(--text)', width: '90px' },
  btn:     { padding: '8px 18px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: 'var(--accent)', color: '#fff' },
  btnG:    { padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '12px', background: 'var(--bg)', color: 'var(--text)' },
  btnOn:   { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  muted:   { color: 'var(--text-muted)', fontSize: '12px' },
  err:     { color: 'var(--error)', fontSize: '13px', margin: '8px 0' },
  runs:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' },
  tiles:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: '8px' },
  tile:    { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px' },
  tl:      { fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" },
  tv:      { fontSize: '17px', fontWeight: 600, fontFamily: "'DM Mono', monospace" },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'DM Mono', monospace" },
  th:      { textAlign: 'right', color: 'var(--text-muted)', fontWeight: 500, padding: '3px 4px', borderBottom: '1px solid var(--border)' },
  td:      { textAlign: 'right', padding: '3px 4px', borderBottom: '1px solid var(--border)' },
};

const col = v => v > 0 ? 'var(--success)' : v < 0 ? 'var(--error)' : 'var(--text-muted)';
const fx  = (v, d = 2) => v == null ? '—' : Number(v).toFixed(d);
const store = {
  get: (k, dflt) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : dflt; } catch { return dflt; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage unavailable */ } },
};
const loadCfg = () => {
  const v = store.get(STORE, {});
  return { ...DEFAULTS, ...v, st: { ...DEFAULTS.st, ...(v.st || {}) }, td: { ...DEFAULTS.td, ...(v.td || {}) }, raid: { ...DEFAULTS.raid, ...(v.raid || {}) } };
};
const addDays = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const sleep = ms => new Promise(r => setTimeout(r, ms));

// The request body the engine expects (smc_lab.clean rebuilds it server-side).
function body(cfg, from, to, extra = {}) {
  const { testOn, testFrom, testTo, filters, st, td, raid, ...rest } = cfg;
  return { ...rest, from, to, filters: cfg.model === 'structure' ? [] : filters.map(serialiseCond) /* structure ignores filters */, smc: { structure: st, topdown: td, raid }[cfg.model], ...extra };
}

// Start a job (or resume one by id) and wait for its result. Network errors and the
// phone sleeping don't end the wait; only a real server answer or 30 minutes does.
async function waitFor(job) {
  const until = Date.now() + 30 * 60 * 1000;
  while (Date.now() < until) {
    await sleep(2500);
    try {
      const res = await api.get(`/smc/result/${job}`);
      if (res.status === 200) return res.data;
    } catch (e) {
      const st = e.response?.status;
      if (st === 404 || st === 400) return { error: e.response?.data?.error || 'Run not found' };
      // otherwise: offline / asleep / server busy - keep waiting
    }
  }
  return { error: 'Still running after 30 minutes - press Run again later to check' };
}

function Num({ label, value, onChange, step = 'any', width }) {
  return (
    <label style={s.label}>{label}
      <input style={{ ...s.input, ...(width ? { width } : {}) }} type="number" step={step} value={value}
             onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
    </label>
  );
}

function Equity({ curve, start }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!HC || !ref.current || !curve.length) return;
    const chart = HC.chart(ref.current, {
      credits: { enabled: false }, title: { text: '' }, legend: { enabled: false },
      chart: { height: 180, backgroundColor: 'transparent' },
      xAxis: { type: 'datetime' }, yAxis: { title: { text: '' } },
      series: [{ type: 'line', name: 'Equity', lineWidth: 1.5, color: '#7C3AED',
                 data: [[Date.parse(curve[0][0]), start], ...curve.map(([d, v]) => [Date.parse(d), v])] }],
      tooltip: { xDateFormat: '%e %b %Y', valueDecimals: 0, valuePrefix: '$' },
    });
    return () => chart.destroy();
  }, [curve, start]);
  return curve.length ? <div ref={ref} /> : <div style={s.muted}>No closed trades.</div>;
}

function Result({ name, r, onChart }) {
  const [openTrade, setOpenTrade] = useState(null);
  if (r.error) return <div style={s.section}><div style={s.title}>{name}</div><div style={s.err}>{r.error}</div></div>;
  const sm = r.summary;
  const model = r.config.model || 'raid';
  const tile = (label, v, c) => <div key={label} style={s.tile}><div style={s.tl}>{label}</div><div style={{ ...s.tv, color: c }}>{v}</div></div>;
  // Hide breakdowns that don't apply to this model (a single "-" group).
  const tables = BREAKDOWNS.filter(([k]) => { const g = Object.keys(r.breakdowns[k] || {}); return g.length && !(g.length === 1 && ['-', 'None'].includes(g[0])); });
  const tf = `${r.config.trend_tf || '—'} / ${r.config.liq_tf} / ${r.config.exec_tf}`;
  const pct = model === 'structure';              // structure model: 1R = 1% of the position
  const u = pct ? '%' : 'R';
  const what = pct ? `BOS / CHoCH on ${r.config.exec_tf}${r.config.exec_tf === '1d' ? ' (end of day)' : ''}, swing ${r.config.smc.swing_len}`
    : model === 'topdown' ? `top-down ${tf}, hold ${r.config.smc.hold}` : 'raid & shift';
  return (
    <div style={s.section}>
      <div style={s.title}>{name} <span style={s.muted}>{r.config.from} → {r.config.to} · {what}</span></div>
      <div style={s.tiles}>
        {tile('Trades', sm.trades)}
        {tile('Win rate', sm.trades ? `${fx(sm.win_rate, 1)}%` : '—')}
        {tile(pct ? 'Avg trade %' : 'Avg R', fx(sm.avg_r, 3), col(sm.avg_r))}
        {tile('Costs / trade', sm.trades ? `${fx(sm.avg_cost_r, 3)}${u}` : '—')}
        {tile(pct ? 'Avg % before costs' : 'Avg R before costs', sm.trades ? fx(sm.avg_r + sm.avg_cost_r, 3) : '—', col(sm.avg_r + sm.avg_cost_r))}
        {tile('Profit factor', fx(sm.profit_factor))}
        {tile('Return', `${fx(sm.total_return_pct, 1)}%`, col(sm.total_return_pct))}
        {tile('Max DD', `${fx(sm.max_drawdown_pct, 1)}%`)}
        {tile('CAGR', `${fx(sm.cagr_pct, 1)}%`, col(sm.cagr_pct))}
        {Object.entries(r.benchmarks || {}).map(([tk, v]) => tile(`Hold ${tk.split(':')[0]}`, `${fx(v, 1)}%`, col(v)))}
      </div>
      {sm.trades > 0 && (
        <div style={{ ...s.muted, margin: '8px 0' }}>
          Losing trades went <b>+{fx(sm.losers_mfe_r)}{u}</b> in our favour on average before they lost; winning trades went <b>−{fx(sm.winners_mae_r)}{u}</b> against us first{pct ? ' (QQQ moves)' : ''}.
        </div>
      )}
      <div style={{ ...s.muted, margin: '8px 0' }}>
        <b>Funnel</b> {(FUNNEL[model] || []).map(k => `${k} ${r.funnel?.[k] || 0}`).join(' → ')} → filled {r.counts.filled || 0}<br />
        {[...Object.entries(r.funnel || {}).filter(([k]) => !(FUNNEL[model] || []).includes(k)), ...Object.entries(r.counts).filter(([k]) => !['setups', 'filled'].includes(k))]
          .map(([k, v]) => `${k} ${v}`).join(' · ')}
      </div>
      <Equity curve={r.equity} start={sm.start_equity} />
      {tables.map(([k, label]) => (
        <div key={k}>
          <div style={s.sub}>{label}</div>
          <table style={s.table}><thead><tr>
            <th style={{ ...s.th, textAlign: 'left' }}>Group</th><th style={s.th}>Trades</th><th style={s.th}>Win%</th><th style={s.th}>Avg {u}</th><th style={s.th}>Total {u}</th>
          </tr></thead><tbody>
            {Object.entries(r.breakdowns[k]).map(([g, v]) => (
              <tr key={g}><td style={{ ...s.td, textAlign: 'left' }}>{g}</td><td style={s.td}>{v.trades}</td>
                <td style={s.td}>{fx(v.win_rate, 1)}</td><td style={{ ...s.td, color: col(v.avg_r) }}>{fx(v.avg_r, 3)}</td>
                <td style={{ ...s.td, color: col(v.total_r) }}>{fx(v.total_r, 1)}</td></tr>
            ))}
          </tbody></table>
        </div>
      ))}
      <div style={s.sub}>Trades (newest first, tap for detail)</div>
      <table style={s.table}><tbody>
        {[...r.trades].reverse().slice(0, 300).map((t, i) => (
          <React.Fragment key={i}>
            <tr style={{ cursor: 'pointer' }} onClick={() => setOpenTrade(openTrade === i ? null : i)}>
              <td style={{ ...s.td, textAlign: 'left' }}>{t.entry_ts.slice(0, 16).replace('T', ' ')}</td>
              <td style={s.td}>{t.dir === 1 ? 'L' : 'S'} {t.vehicle.split(':')[0]}</td>
              <td style={s.td}>{t.exit_reason}</td>
              <td style={{ ...s.td, color: col(t.r) }}>{fx(t.r)}{u}</td>
            </tr>
            {openTrade === i && (
              <tr><td colSpan={4} style={{ ...s.td, textAlign: 'left', color: 'var(--text-muted)', background: 'var(--bg)' }}>
                Signal entry {fx(t.entry_sig, 2)}{t.stop != null ? ` stop ${fx(t.stop, 2)}` : ''}{t.target != null ? ` target ${fx(t.target, 2)}` : ''}{t.rr ? ` (R:R ${t.rr})` : ''} · {t.window_label}<br />
                {t.shares} {t.vehicle} @ {t.entry_px} → {t.exit_px} at {t.exit_ts.slice(0, 16).replace('T', ' ')} · P&L ${fx(t.pnl, 0)} · costs {fx(t.cost_r, 3)}{u}<br />
                Best {fx(t.mfe_r)}{u} in favour, worst {fx(t.mae_r)}{u} against<br />
                {Object.entries(t.features).map(([k, v]) => `${k}=${v}`).join(' · ')}<br />
                <button style={{ ...s.btnG, marginTop: '6px' }} onClick={() => onChart(t.ticker, t.entry_ts.slice(0, 10))}>Chart this day</button>
              </td></tr>
            )}
          </React.Fragment>
        ))}
      </tbody></table>
    </div>
  );
}

export default function SmcLab() {
  const [cfg, setCfg]         = useState(loadCfg);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState('');
  const [adv, setAdv]         = useState(false);
  const [chartReq, setChartReq] = useState({ ticker: 'QQQ:US', day: '2025-10-01', days: 1 });
  const [chart, setChart]       = useState(null);
  const [chartBusy, setChartBusy] = useState(false);
  const [chartErr, setChartErr] = useState('');
  const chartRef = useRef(null);

  useEffect(() => { store.set(STORE, cfg); }, [cfg]);

  const set = patch => setCfg(c => ({ ...c, ...patch }));
  const setTd = patch => setCfg(c => ({ ...c, td: { ...c.td, ...patch } }));
  const setRaid = patch => setCfg(c => ({ ...c, raid: { ...c.raid, ...patch } }));
  const toggle = (list, v) => list.includes(v) ? list.filter(x => x !== v) : [...list, v];

  // Wait for a set of jobs (kept in this browser until they finish).
  const collect = useCallback(async jobs => {
    setRunning(true); setError(''); setResults([]);
    try {
      setResults(await Promise.all(jobs.map(j => waitFor(j.job))));
    } finally {
      store.set(JOBS, null);
      setRunning(false);
    }
  }, []);

  // A run started before a reload or while the phone slept: pick it back up.
  useEffect(() => { const jobs = store.get(JOBS, null); if (jobs?.length) collect(jobs); }, [collect]);

  const run = async () => {
    setError('');
    try {
      const ranges = [[cfg.from, cfg.to], ...(cfg.testOn ? [[cfg.testFrom, cfg.testTo]] : [])];
      const jobs = [];
      for (const [f, t] of ranges) jobs.push({ job: (await api.post('/smc/backtest', body(cfg, f, t))).data.job });
      store.set(JOBS, jobs);
      await collect(jobs);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Run failed');
    }
  };

  const loadChart = async (ticker = chartReq.ticker, day = chartReq.day, n = chartReq.days) => {
    const days = Math.min(Math.max(1, Number(n) || 1), maxDays);
    setChartReq(c => ({ ...c, ticker, day, days }));
    setChartBusy(true); setChartErr(''); setChart(null);
    chartRef.current?.scrollIntoView({ behavior: 'smooth' });
    try {
      const job = (await api.post('/smc/backtest', body(cfg, day, addDays(day, days - 1), { chart: { ticker } }))).data.job;
      const r = await waitFor(job);
      if (r.error) setChartErr(r.error); else setChart(r.chart);
    } catch (e) {
      setChartErr(e.response?.data?.error || e.message || 'Chart failed');
    } finally {
      setChartBusy(false);
    }
  };

  const tickers = TICKERS[cfg.mode];
  const td = cfg.model === 'topdown';
  const st = cfg.model === 'structure';
  const eod = st && cfg.exec_tf === '1d';   // end-of-day mode: daily bars only, no windows / flat-by-close
  const maxDays = st ? (CHART_DAYS[cfg.exec_tf] || 5) : 5;
  const preset = PRESETS.find(p => p[2] === cfg.trend_tf && p[3] === cfg.liq_tf && p[4] === cfg.exec_tf);
  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.heading}>SMC Lab</div>
        <div style={{ ...s.muted, marginBottom: '16px' }}>
          Smart Money Concepts backtest on minute data. Pick a model and press Run; everything else has defaults. Signals come from the ticker; leveraged mode trades the 3x pair (TQQQ long / SQQQ for shorts), single mode trades the ticker itself (shorts sold short). Runs keep going if your phone sleeps - come back to this page to see the result.
        </div>

        <div style={s.section}>
          <div style={s.title}>Setup</div>
          <div style={s.row}>
            <label style={s.label}>Model
              <select style={{ ...s.input, width: '160px' }} value={cfg.model} onChange={e => set({ model: e.target.value, ...MODEL_DEFAULTS[e.target.value] })}>
                <option value="structure">BOS / CHoCH only</option><option value="topdown">Top-down structure</option><option value="raid">Raid &amp; shift</option>
              </select></label>
            <label style={s.label}>Vehicle
              <select style={{ ...s.input, width: '150px' }} value={cfg.mode}
                      onChange={e => set({ mode: e.target.value, signals: cfg.signals.filter(t => TICKERS[e.target.value].includes(t)).length ? cfg.signals.filter(t => TICKERS[e.target.value].includes(t)) : ['QQQ:US'] })}>
                <option value="leveraged">Leveraged pair</option><option value="single">Single ticker</option>
              </select></label>
            {tickers.map(t => (
              <label key={t} style={s.check}><input type="checkbox" checked={cfg.signals.includes(t)} onChange={() => set({ signals: toggle(cfg.signals, t) })} />{t.split(':')[0]}</label>
            ))}
          </div>

          <div style={s.sub}>Dates</div>
          <div style={s.row}>
            <label style={s.label}>From<input style={{ ...s.input, width: '140px' }} type="date" value={cfg.from} onChange={e => set({ from: e.target.value })} /></label>
            <label style={s.label}>To<input style={{ ...s.input, width: '140px' }} type="date" value={cfg.to} onChange={e => set({ to: e.target.value })} /></label>
            <label style={s.check}><input type="checkbox" checked={cfg.testOn} onChange={e => set({ testOn: e.target.checked })} />Also test on</label>
            {cfg.testOn && <>
              <label style={s.label}>From<input style={{ ...s.input, width: '140px' }} type="date" value={cfg.testFrom} onChange={e => set({ testFrom: e.target.value })} /></label>
              <label style={s.label}>To<input style={{ ...s.input, width: '140px' }} type="date" value={cfg.testTo} onChange={e => set({ testTo: e.target.value })} /></label>
            </>}
          </div>

          {st ? (
            <>
              <div style={s.sub}>Structure</div>
              <div style={s.row}>
                <label style={s.label}>Candles
                  <select style={{ ...s.input, width: '140px' }} value={cfg.exec_tf} onChange={e => set({ exec_tf: e.target.value })}>
                    {FRAMES.map(f => <option key={f} value={f}>{f}</option>)}<option value="1d">1d (end of day)</option></select></label>
                <Num label="Swing length" value={cfg.st.swing_len} onChange={v => set({ st: { ...cfg.st, swing_len: v } })} step="1" />
                <label style={s.label}>Direction
                  <select style={{ ...s.input, width: '150px' }} value={cfg.direction} onChange={e => set({ direction: e.target.value })}>
                    <option value="both">TQQQ and SQQQ</option><option value="long">long only (cash when bearish)</option><option value="short">short only</option></select></label>
              </div>
              <div style={{ ...s.muted, marginTop: '6px' }}>Holds TQQQ after a bullish break and SQQQ after a bearish one, switching at each CHoCH (a BOS just confirms the side). {eod ? 'End of day: uses daily bars only (no minute data), so it can go back to the ETFs\' first day (TQQQ/SQQQ February 2010); a signal at the close is filled at the next day\'s open.' : 'Trades only in the regular session.'} No stop or target. Shares can be fractional; commission is charged on the real (as-traded) share count. Swing length = candles each side that make a swing high/low.</div>
            </>
          ) : td ? (
            <>
              <div style={s.sub}>Timeframes</div>
              <div style={{ ...s.row, marginBottom: '8px' }}>
                {PRESETS.map(([k, label, b, z, e]) => (
                  <button key={k} style={{ ...s.btnG, ...(preset?.[0] === k ? s.btnOn : {}) }}
                          onClick={() => set({ trend_tf: b, liq_tf: z, exec_tf: e })}>{label} {b}/{z}/{e}</button>
                ))}
              </div>
              <div style={s.row}>
                <label style={s.label}>Bias
                  <select style={s.input} value={cfg.trend_tf || '1h'} onChange={e => set({ trend_tf: e.target.value })}>{FRAMES.concat('1d').map(f => <option key={f}>{f}</option>)}</select></label>
                <label style={s.label}>Zones
                  <select style={s.input} value={cfg.liq_tf} onChange={e => set({ liq_tf: e.target.value })}>{FRAMES.map(f => <option key={f}>{f}</option>)}</select></label>
                <label style={s.label}>Entry
                  <select style={s.input} value={cfg.exec_tf} onChange={e => set({ exec_tf: e.target.value })}>{FRAMES.map(f => <option key={f}>{f}</option>)}</select></label>
              </div>
              <div style={s.sub}>Trading</div>
              <div style={s.row}>
                <label style={s.label}>Hold
                  <select style={{ ...s.input, width: '170px' }} value={cfg.td.hold} onChange={e => setTd({ hold: e.target.value })}>
                    <option value="stay">Stay in until bias flips</option><option value="swing">Trade the swings</option></select></label>
                <label style={s.label}>Entry
                  <select style={{ ...s.input, width: '130px' }} value={cfg.td.entry} onChange={e => setTd({ entry: e.target.value })}>
                    <option value="retest">on the retest</option><option value="market">straight away</option></select></label>
                <Num label="Take profit R (0 off)" value={cfg.td.take_profit_r} onChange={v => setTd({ take_profit_r: v })} />
                <label style={s.check}><input type="checkbox" checked={cfg.td.require_zone} onChange={e => setTd({ require_zone: e.target.checked })} />Pullback must reach a zone</label>
                <label style={s.label}>Direction
                  <select style={s.input} value={cfg.direction} onChange={e => set({ direction: e.target.value })}>
                    <option value="both">both</option><option value="long">long</option><option value="short">short</option></select></label>
              </div>
            </>
          ) : (
            <>
              <div style={s.sub}>Liquidity to watch</div>
              <div style={s.row}>
                {SOURCES.map(([k, label]) => (
                  <label key={k} style={s.check}><input type="checkbox" checked={cfg.raid.sources.includes(k)} onChange={() => setRaid({ sources: toggle(cfg.raid.sources, k) })} />{label}</label>
                ))}
              </div>
              <div style={s.sub}>Candles &amp; filters</div>
              <div style={s.row}>
                <label style={s.label}>Raid / shift
                  <select style={s.input} value={cfg.exec_tf} onChange={e => set({ exec_tf: e.target.value })}>{['1m', '5m'].map(f => <option key={f}>{f}</option>)}</select></label>
                <label style={s.label}>Liquidity
                  <select style={s.input} value={cfg.liq_tf} onChange={e => set({ liq_tf: e.target.value })}>{FRAMES.map(f => <option key={f}>{f}</option>)}</select></label>
                <label style={s.label}>Trend filter
                  <select style={s.input} value={cfg.trend_tf || ''} onChange={e => set({ trend_tf: e.target.value || null })}>
                    <option value="">off</option>{FRAMES.concat('1d').map(f => <option key={f}>{f}</option>)}</select></label>
                <label style={s.label}>Direction
                  <select style={s.input} value={cfg.direction} onChange={e => set({ direction: e.target.value })}>
                    <option value="both">both</option><option value="long">long</option><option value="short">short</option></select></label>
                <Num label="Min R:R" value={cfg.raid.min_rr} onChange={v => setRaid({ min_rr: v })} />
                <label style={s.label}>Entry
                  <select style={{ ...s.input, width: '130px' }} value={cfg.raid.entry} onChange={e => setRaid({ entry: e.target.value })}>
                    <option value="ce">middle of gap</option><option value="edge">near edge</option></select></label>
              </div>
            </>
          )}

          {!eod && <>
          <div style={{ ...s.muted, margin: '10px 0 4px' }}>Entry windows (NY time) - new trades only start inside these</div>
          {cfg.windows.map((w, i) => (
            <div key={i} style={{ ...s.row, marginBottom: '6px' }}>
              <input style={s.input} type="time" value={w[0]} onChange={e => set({ windows: cfg.windows.map((x, j) => j === i ? [e.target.value, x[1]] : x) })} />
              <span style={s.muted}>to</span>
              <input style={s.input} type="time" value={w[1]} onChange={e => set({ windows: cfg.windows.map((x, j) => j === i ? [x[0], e.target.value] : x) })} />
              <button style={s.btnG} onClick={() => set({ windows: cfg.windows.filter((_, j) => j !== i) })}>✕</button>
            </div>
          ))}
          <div style={s.row}>
            <button style={s.btnG} onClick={() => set({ windows: [...cfg.windows, ['09:30', '16:00']] })}>+ window</button>
            <Num label="Trades per window" value={cfg.max_trades_per_window} onChange={v => set({ max_trades_per_window: v })} step="1" />
          </div>
          </>}

          {!st && <>
          <div style={s.sub}>Indicator filters (all must be true when the setup forms)</div>
          {cfg.filters.map((c, i) => (
            <ConditionRow key={c.id} cond={c} allowTimeframe
                          onChange={nc => set({ filters: cfg.filters.map((x, j) => j === i ? nc : x) })}
                          onRemove={() => set({ filters: cfg.filters.filter((_, j) => j !== i) })} />
          ))}
          <button style={s.btnG} onClick={() => set({ filters: [...cfg.filters, { ...blankCond(), ticker: cfg.signals[0] || 'QQQ:US', timeframe: '15m' }] })}>+ filter</button>
          </>}

          <div style={s.sub}>Risk &amp; costs</div>
          <div style={s.row}>
            <Num label="Start $" value={cfg.start_equity} onChange={v => set({ start_equity: v })} width="110px" />
            {!st && <Num label="Risk % / trade" value={cfg.risk_pct} onChange={v => set({ risk_pct: v })} />}
            <Num label="Max positions" value={cfg.max_positions} onChange={v => set({ max_positions: v })} step="1" />
            <Num label="Max exposure ×" value={cfg.max_exposure} onChange={v => set({ max_exposure: v })} />
            <Num label="$ / share" value={cfg.commission_per_share} onChange={v => set({ commission_per_share: v })} />
            <Num label="Min $ / order" value={cfg.commission_min} onChange={v => set({ commission_min: v })} />
            <Num label="Slippage bps" value={cfg.slippage_bps} onChange={v => set({ slippage_bps: v })} />
            {!st && <Num label="Break-even at R (0 off)" value={cfg.breakeven_r} onChange={v => set({ breakeven_r: v })} />}
            {!eod && <label style={s.check}><input type="checkbox" checked={cfg.flat_eod} onChange={e => set({ flat_eod: e.target.checked })} />Flat by the close (off = hold overnight)</label>}
          </div>

          {!st && <div style={{ ...s.sub, cursor: 'pointer' }} onClick={() => setAdv(!adv)}>{adv ? '▾' : '▸'} SMC detail</div>}
          {adv && !st && (td ? (
            <div style={s.row}>
              <Num label="Bias swing length" value={cfg.td.bias_swing_len} onChange={v => setTd({ bias_swing_len: v })} step="1" />
              <Num label="Zone swing length" value={cfg.td.zone_swing_len} onChange={v => setTd({ zone_swing_len: v })} step="1" />
              <Num label="Entry swing length" value={cfg.td.entry_swing_len} onChange={v => setTd({ entry_swing_len: v })} step="1" />
              <Num label="Min zone gap ATR×" value={cfg.td.zone_min_atr} onChange={v => setTd({ zone_min_atr: v })} />
              <Num label="Zone life (candles)" value={cfg.td.zone_max_bars} onChange={v => setTd({ zone_max_bars: v })} step="1" />
              <Num label="Stop buffer ATR×" value={cfg.td.stop_buffer_atr} onChange={v => setTd({ stop_buffer_atr: v })} />
              <Num label="Min stop ATR×" value={cfg.td.min_risk_atr} onChange={v => setTd({ min_risk_atr: v })} />
              <Num label="Order expiry (candles)" value={cfg.td.expiry} onChange={v => setTd({ expiry: v })} step="1" />
            </div>
          ) : (
            <div style={s.row}>
              <Num label="Opening range (min)" value={cfg.raid.opening_range_min} onChange={v => setRaid({ opening_range_min: v })} step="1" />
              <Num label="Level life (days)" value={cfg.raid.level_max_days} onChange={v => setRaid({ level_max_days: v })} step="1" />
              <Num label="Liquidity swing length" value={cfg.raid.htf_swing_len} onChange={v => setRaid({ htf_swing_len: v })} step="1" />
              <Num label="Equal level ATR×" value={cfg.raid.eq_tol_atr} onChange={v => setRaid({ eq_tol_atr: v })} />
              <Num label="Shift swing length" value={cfg.raid.ltf_swing_len} onChange={v => setRaid({ ltf_swing_len: v })} step="1" />
              <Num label="Shift within (candles)" value={cfg.raid.sweep_window} onChange={v => setRaid({ sweep_window: v })} step="1" />
              <Num label="Min gap ATR×" value={cfg.raid.fvg_min_atr} onChange={v => setRaid({ fvg_min_atr: v })} />
              <Num label="Stop buffer ATR×" value={cfg.raid.stop_buffer_atr} onChange={v => setRaid({ stop_buffer_atr: v })} />
              <Num label="Order expiry (candles)" value={cfg.raid.expiry} onChange={v => setRaid({ expiry: v })} step="1" />
            </div>
          ))}

          <div style={{ ...s.row, marginTop: '16px' }}>
            <button style={{ ...s.btn, opacity: running ? 0.5 : 1 }} disabled={running} onClick={run}>{running ? 'Running…' : 'Run'}</button>
            <button style={s.btnG} disabled={running} onClick={() => setCfg(DEFAULTS)}>Reset to defaults</button>
          </div>
          {running && <div style={{ ...s.muted, marginTop: '6px' }}>Long runs on 1-minute candles take a few minutes. It's fine to switch apps - the result will be here when you come back.</div>}
          {error && <div style={s.err}>{error}</div>}
        </div>

        <div style={s.section} ref={chartRef}>
          <div style={s.title}>Chart</div>
          <div style={{ ...s.muted, marginBottom: '8px' }}>
            {st
              ? 'The structure on one ticker, using the settings above: BOS / CHoCH lines, background green while holding TQQQ and red while holding SQQQ, and trades (blue triangle in, green/red circle out).'
              : td
              ? 'What the model saw on one ticker over 1–5 days, using the settings above, on the entry candles: bias BOS/CHoCH (thick lines) and entry BOS/CHoCH (thin), zones (green/red boxes), bias shading (green up, red down), setups (entry and stop while the order was live), setups thrown out (grey dots, tap for why) and trades (blue triangle in, green/red circle out).'
              : 'What the model saw on one ticker over 1–5 days, using the settings above: liquidity levels (dashed, labelled PD/PW/ON/OR/EQ/SW + H or L; purple once raided), raids (purple diamonds), shifts (MSS lines), the entry gap (box), setups (entry / stop / target while live), setups thrown out (grey dots, tap for why) and trades (blue triangle in, green/red circle out). The background shows the trend filter.'}
            {' '}Tap a legend item to hide it, or open a trade below and press "Chart this day".
          </div>
          <div style={s.row}>
            <label style={s.label}>Ticker
              <select style={s.input} value={chartReq.ticker} onChange={e => setChartReq({ ...chartReq, ticker: e.target.value })}>
                {cfg.signals.map(t => <option key={t}>{t}</option>)}</select></label>
            <label style={s.label}>From<input style={{ ...s.input, width: '140px' }} type="date" value={chartReq.day} onChange={e => setChartReq({ ...chartReq, day: e.target.value })} /></label>
            <Num label={`Days (1-${maxDays})`} value={chartReq.days} onChange={v => setChartReq({ ...chartReq, days: v })} step="1" />
            <button style={{ ...s.btn, opacity: chartBusy ? 0.5 : 1 }} disabled={chartBusy} onClick={() => loadChart()}>{chartBusy ? 'Loading…' : 'Chart'}</button>
            {chart && <>
              <button style={s.btnG} disabled={chartBusy} onClick={() => loadChart(chartReq.ticker, addDays(chartReq.day, -1))}>◀</button>
              <button style={s.btnG} disabled={chartBusy} onClick={() => loadChart(chartReq.ticker, addDays(chartReq.day, 1))}>▶</button>
            </>}
          </div>
          {chartErr && <div style={s.err}>{chartErr}</div>}
          {chart && <div style={{ ...s.muted, margin: '8px 0' }}>{chart.ticker} {chart.timeframe} candles {chart.from} → {chart.to}: {chart.setups.length} setups, {chart.trades.length} trades.</div>}
          {chart && <SmcChart chart={chart} />}
        </div>

        {results.length > 0 && (
          <div style={s.runs}>
            {results.map((r, i) => <Result key={i} name={i === 0 ? (results.length > 1 ? 'Tune range' : 'Result') : 'Test range'} r={r}
                                           onChart={(tk, d) => eod ? loadChart(tk, addDays(d, -90), 180) : loadChart(tk, d)} />)}
          </div>
        )}
      </div>
    </div>
  );
}
